import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Loader2, Palette, MoonStar, BookOpen, Music, ShieldCheck, MailCheck } from "lucide-react";
import { subscribePack, formatApiErrorDetail, fetchPackArt, resolveImage } from "@/lib/api";
import { PACK_DAYS } from "./packContent";

const INSIDE_ICONS = [Palette, BookOpen, Music, MoonStar];

const FreePack = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", email: "", consent: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [art, setArt] = useState({});

  useEffect(() => {
    fetchPackArt().then(setArt).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Please tick the box so we know you'd like our emails.");
      return;
    }
    setLoading(true);
    try {
      await subscribePack(form);
      navigate("/free-pack/pack", { state: { name: form.first_name.trim() } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FFFDF6]" data-testid="free-pack-page">
      {/* slim header */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-4 pt-6 sm:px-6">
        <Link to="/" className="flex items-center gap-2" data-testid="pack-logo">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-[#FFC107] shadow-inner">
            <Star className="h-5 w-5 fill-white text-white" />
          </span>
          <span className="font-display text-base font-semibold tracking-tight sm:text-lg">
            Little Songs. <span className="text-[#0284C7]">Big Faith.</span>
          </span>
        </Link>
        <Link
          to="/"
          data-testid="pack-home-link"
          className="rounded-full border border-[#E2DDD0] bg-white px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:text-slate-900"
        >
          Visit the site
        </Link>
      </header>

      {/* hero */}
      <section className="relative mx-auto grid max-w-6xl gap-12 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:pt-20">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -top-10 -left-24 h-80 w-80 rounded-full bg-[#FEF3C7] blur-3xl" />
          <div className="absolute top-1/2 -right-24 h-80 w-80 rounded-full bg-[#E0F2FE] blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-[#FCD34D] bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-600 shadow-sm" data-testid="pack-badge">
            <Palette className="h-3.5 w-3.5 text-[#FF6B6B]" />
            Free printable pack &bull; ages 2&ndash;7
          </span>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl">
            Seven days of little{" "}
            <span className="bg-gradient-to-r from-[#FFC107] via-[#FF6B6B] to-[#9333EA] bg-clip-text text-transparent">
              faith moments
            </span>{" "}
            with your child.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="pack-subheadline">
            The <strong>7-Day Little Faith Activity Pack</strong> — printable colouring pages,
            a gentle bedtime prayer, simple Bible activities and songs to sing together.
            Free for your family, straight to your inbox.
          </p>
          <ul className="mt-7 grid max-w-xl gap-3 sm:grid-cols-2" data-testid="pack-inside-list">
            {[
              { Icon: Palette, text: "7 printable colouring pages" },
              { Icon: BookOpen, text: "Simple Bible activities" },
              { Icon: Music, text: "Songs to sing together" },
              { Icon: MoonStar, text: "A calming bedtime prayer" },
            ].map(({ Icon, text }) => (
              <li key={text} className="flex items-center gap-3 rounded-2xl border border-[#E2DDD0] bg-white/80 px-4 py-3 text-sm font-semibold text-slate-700">
                <Icon className="h-4.5 w-4.5 h-[18px] w-[18px] text-[#0284C7]" />
                {text}
              </li>
            ))}
          </ul>
          <p className="mt-6 font-hand text-2xl text-slate-500">
            ten quiet minutes a day — that&apos;s all it takes
          </p>
        </motion.div>

        {/* signup card */}
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="rounded-3xl border border-[#E2DDD0] bg-white p-8 shadow-[0_24px_48px_-12px_rgba(14,165,233,0.18)]" data-testid="pack-form-card">
            <h2 className="font-display text-2xl font-bold text-slate-900">Get your free pack</h2>
            <p className="mt-1 text-sm text-slate-500">
              Opens instantly — and a copy lands in your inbox.
            </p>
            <form onSubmit={submit} className="mt-5 grid gap-3" data-testid="pack-form">
              <input
                required
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                data-testid="pack-name-input"
                placeholder="Your first name"
                maxLength={60}
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="pack-email-input"
                placeholder="Your email address"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
              />
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3" data-testid="pack-consent-label">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  data-testid="pack-consent-checkbox"
                  className="mt-0.5 h-5 w-5 shrink-0 accent-[#16A34A]"
                />
                <span className="text-sm leading-snug text-slate-600">
                  Send me Little Songs, Big Faith activities, new songs and occasional offers.
                </span>
              </label>
              {error && (
                <p className="rounded-2xl bg-[#FFE5E5] px-4 py-3 text-sm font-semibold text-[#DC2626]" data-testid="pack-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="pack-submit-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFC107] py-4 font-display text-base font-semibold text-slate-900 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.45)] transition-all hover:-translate-y-0.5 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Get FREE Christian activities for your little one &rarr;
              </button>
              <p className="flex items-center justify-center gap-1.5 text-center text-xs text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5 text-[#16A34A]" />
                Emails are for grown-ups only &bull; unsubscribe anytime with one click
              </p>
            </form>
          </div>
        </motion.div>
      </section>

      {/* what's inside preview */}
      <section className="mx-auto max-w-6xl px-4 pb-24 sm:px-6">
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          A peek inside your week
        </h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="pack-preview-grid">
          {PACK_DAYS.slice(0, 4).map((d) => (
            <div key={d.day} className="rounded-3xl border border-[#E2DDD0] bg-white p-5 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.1)]">
              <span className="inline-block rounded-full px-3 py-1 text-xs font-bold text-white" style={{ backgroundColor: d.color }}>
                Day {d.day}
              </span>
              {art[String(d.day)] ? (
                <img
                  src={resolveImage(art[String(d.day)])}
                  alt={`${d.title} colouring sheet`}
                  className="mx-auto mt-3 h-24 w-24 rounded-xl object-contain"
                />
              ) : (
                <d.Art className="mx-auto mt-3 h-24 w-24" />
              )}
              <h3 className="mt-3 font-display text-base font-bold text-slate-900">{d.title}</h3>
              <p className="mt-1 text-xs font-semibold text-slate-400">{d.verse}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-col items-center justify-between gap-4 rounded-3xl border border-[#E2DDD0] bg-white p-6 sm:flex-row" data-testid="pack-how-it-works">
          {["Enter your email above", "Your pack opens instantly", "A copy lands in your inbox"].map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-900 font-display text-sm font-bold text-white">{i + 1}</span>
              <p className="text-sm font-semibold text-slate-700">{s}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 flex items-center justify-center gap-2 text-center text-sm text-slate-400">
          <MailCheck className="h-4 w-4 text-[#16A34A]" />
          Made for parents and guardians to share with little ones — never marketed to children.
        </p>
      </section>
    </main>
  );
};

export default FreePack;
